import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import AdminTopBar from './AdminTopBar';
import { Box, Paper, CircularProgress, Alert } from '@mui/material';
import Dashboard from './Dashboard';
import UserManagement from './UserManagement';
import RateLimiting from './RateLimiting';
import QueryLogs from './QueryLogs';
import AuditLogs from './AuditLogs';
import SystemMetrics from './SystemMetrics';
import Alerts from './Alerts';
import DataSources from './DataSources';
import Settings from './Settings';
import FeedbackAnalytics from './FeedbackAnalytics';
import ChunkSettings from './ChunkSettings';
import EmbeddingsManager from './EmbeddingsManager';
import ModelConfiguration from './ModelConfiguration';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activePath, setActivePath] = useState('/admin/UserManagement');
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  
  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('adminToken');
    if (!token) {
      // Redirect to login if no token is present
      navigate('/admin/login');
      return;
    }
    
    // Verify token validity
    try {
      // Basic token validation
      const parts = token.split('.');
      if (parts.length !== 3) {
        setAuthError(true);
        localStorage.removeItem('adminToken');
        setTimeout(() => navigate('/admin/login'), 2000);
        return;
      }
      
      // Check if token has expired
      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      
      if (Date.now() >= exp) {
        setAuthError(true);
        localStorage.removeItem('adminToken');
        setTimeout(() => navigate('/admin/login'), 2000);
        return;
      }
      
      // Token is valid
      setLoading(false);
      setAuthError(false);
    } catch (error) {
      console.error('Token verification error:', error);
      setAuthError(true);
      localStorage.removeItem('adminToken');
      setTimeout(() => navigate('/admin/login'), 2000);
    }
    
    // Update active path based on current location
    setActivePath(location.pathname);
  }, [location.pathname, navigate]);

  // Render the appropriate component based on the active path
  const renderContent = () => {
    switch (activePath) {
      case '/admin/dashboard':
        return <Dashboard />;
      case '/admin/users':
        return <UserManagement />;
      case '/admin/query-logs':
        return <QueryLogs />;
      case '/admin/audit-logs':
        return <AuditLogs />;
      case '/admin/metrics':
        return <SystemMetrics />;
      case '/admin/alerts':
        return <Alerts />;
      case '/admin/data-sources':
        return <DataSources />;
      case '/admin/settings':
        return <Settings />;
      case '/admin/feedback-analytics':
        return <FeedbackAnalytics />;
      case '/admin/embeddings':
        return <EmbeddingsManager />;
      case '/admin/chunk-settings':
        return <ChunkSettings />;
      case '/admin/model-configuration':
        return <ModelConfiguration />;
      case '/admin/rate-limiting':
        return <RateLimiting />;
      default:
        return <UserManagement />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (authError) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error">Authentication error. Please log in again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AdminSidebar activePath={activePath} onNavigate={setActivePath} />
      <Box component="main" sx={{ flexGrow: 1, height: '100vh', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <AdminTopBar title={getPageTitle(activePath)} />
        <Paper sx={{ m: 2, p: 2, flexGrow: 1, overflow: 'auto' }}>
          {renderContent()}
        </Paper>
      </Box>
    </Box>
  );
};

// Helper function to get the page title from the path
const getPageTitle = (path) => {
  switch (path) {
    case '/admin/dashboard':
      return 'Dashboard';
    case '/admin/users':
      return 'User Management';
    case '/admin/query-logs':
      return 'Query Logs';
    case '/admin/audit-logs':
      return 'Audit Logs';
    case '/admin/metrics':
      return 'System Metrics';
    case '/admin/alerts':
      return 'Alerts';
    case '/admin/data-sources':
      return 'Data Sources';
    case '/admin/settings':
      return 'Settings';
    case '/admin/feedback-analytics':
      return 'Feedback Analytics';
    case '/admin/embeddings':
      return 'Embeddings Manager';
    case '/admin/chunk-settings':
      return 'Chunk Settings';
    case '/admin/model-configuration':
      return 'Model Configuration';
    case '/admin/rate-limiting':
      return 'Rate Limiting';
    default:
      return 'Admin Panel';
  }
};

export default AdminLayout;